"use client";

import * as React from "react";
import type { Environment } from "../../../types";
import { fetchRemoteConfig, type AppsConfig } from "../../../lib/remote-config";

/**
 * 토큰 상세화면 각 섹션의 노출 여부를 S3(CDN) 원격 설정으로 제어.
 *
 * 설정은 기존 `apps.json`의 `tokenDetail.sections`에 둔다. 값은 섹션별 boolean.
 * env별 차이는 apps.json 파일 자체가 환경마다 분리돼 있어(stg-contents vs
 * contents) 파일별로 값을 다르게 주면 되므로, 값은 단순 boolean으로만 받는다.
 *
 * ```json
 * {
 *   "tokenDetail": {
 *     "sections": {
 *       "chart":    true,
 *       "overview": true,
 *       "social":   true
 *     }
 *   }
 * }
 * ```
 *
 * 기본값(설정 로딩 전 / fetch 실패 / 키 누락): **history·balance만 노출**,
 * chart·overview·social 은 숨김 → S3에서 명시적으로 켜야 노출된다(opt-in).
 * `balance` 섹션은 My Balance + Send 버튼을 함께 포함하므로 하나로 토글된다.
 */
export type TokenDetailSectionKey =
  | "chart"
  | "balance"
  | "overview"
  | "social"
  | "history";

export type TokenDetailSectionVisibility = Record<
  TokenDetailSectionKey,
  boolean
>;

/** 기본값: history·balance만 노출, 나머지는 S3에서 opt-in. */
const DEFAULT_VISIBILITY: TokenDetailSectionVisibility = {
  chart: false,
  balance: true,
  overview: false,
  social: false,
  history: true,
};

const SECTION_KEYS = Object.keys(DEFAULT_VISIBILITY) as TokenDetailSectionKey[];

const APPS_CONFIG_FILE = "apps.json";

export interface TokenDetailVisibilityResult {
  visibility: TokenDetailSectionVisibility;
  /** 원격 설정 fetch가 끝났는지. false면 아직 기본값을 쓰는 중. */
  isLoaded: boolean;
}

export function useTokenDetailVisibility(
  env: Environment,
): TokenDetailVisibilityResult {
  const [state, setState] = React.useState<TokenDetailVisibilityResult>({
    visibility: DEFAULT_VISIBILITY,
    isLoaded: false,
  });

  React.useEffect(() => {
    let alive = true;
    fetchRemoteConfig<AppsConfig>(APPS_CONFIG_FILE, env).then((config) => {
      if (!alive) return;
      const raw = config?.tokenDetail?.sections;
      const visibility: TokenDetailSectionVisibility = { ...DEFAULT_VISIBILITY };
      if (raw) {
        for (const key of SECTION_KEYS) {
          const value = raw[key];
          if (typeof value === "boolean") visibility[key] = value;
        }
      }
      setState({ visibility, isLoaded: true });
    });
    return () => {
      alive = false;
    };
  }, [env]);

  return state;
}
